import React, { useEffect, useMemo, useState } from "react";
import { Check, Plug, Plus, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { API_BASE } from "@/lib/api";
import ApiKeys from "@/components/dashboard/ApiKeys";
import BlotatoCard from "@/components/dashboard/BlotatoCard";

const META_LOGIN_URL = `${API_BASE}/connections/meta/login`;
const GOOGLE_LOGIN_URL = `${API_BASE}/integrations/google/login`;
const TIKTOK_LOGIN_URL = `${API_BASE}/connections/tiktok/login`;
const YOUTUBE_LOGIN_URL = `${API_BASE}/connections/youtube/login`;
const LINKEDIN_LOGIN_URL = `${API_BASE}/connections/linkedin/login`;
const THREADS_LOGIN_URL = `${API_BASE}/connections/threads/login`;
const PINTEREST_LOGIN_URL = `${API_BASE}/connections/pinterest/login`;

const Connections = () => {
  const [channels, setChannels] = useState([]);
  const [connections, setConnections] = useState({});
  const [metaStatus, setMetaStatus] = useState(null);
  const [googleStatus, setGoogleStatus] = useState(null);
  const [tiktokStatus, setTiktokStatus] = useState(null);
  const [youtubeStatus, setYoutubeStatus] = useState(null);
  const [linkedinStatus, setLinkedinStatus] = useState(null);
  const [threadsStatus, setThreadsStatus] = useState(null);
  const [pinterestStatus, setPinterestStatus] = useState(null);
  const [busy, setBusy] = useState(null);
  const [handle, setHandle] = useState({});

  const load = async () => {
    try {
      const [cRes, mRes, mStatus, gStatus, tStatus, yStatus, lStatus, thStatus, pStatus] = await Promise.all([
        api.get("/connections/channels"),
        api.get("/connections"),
        api.get("/connections/meta/status").catch(() => null),
        api.get("/integrations/google/status").catch(() => null),
        api.get("/connections/tiktok/status").catch(() => null),
        api.get("/connections/youtube/status").catch(() => null),
        api.get("/connections/linkedin/status").catch(() => null),
        api.get("/connections/threads/status").catch(() => null),
        api.get("/connections/pinterest/status").catch(() => null),
      ]);
      setChannels(cRes.data || []);
      setConnections(mRes.data || {});
      setMetaStatus(mStatus?.data || null);
      setGoogleStatus(gStatus?.data || null);
      setTiktokStatus(tStatus?.data || null);
      setYoutubeStatus(yStatus?.data || null);
      setLinkedinStatus(lStatus?.data || null);
      setThreadsStatus(thStatus?.data || null);
      setPinterestStatus(pStatus?.data || null);
    } catch (e) {
      // silent
    }
  };

  useEffect(() => {
    load();
    // Surface OAuth round-trip outcomes (backend redirects with these flags).
    const qp = new URLSearchParams(window.location.search);
    if (qp.get("meta_connected")) toast.success("Meta connected. IG & FB are wired in.");
    if (qp.get("meta_error")) toast.error(`Meta error: ${qp.get("meta_error")}`);
    if (qp.get("google_connected")) toast.success("Google Drive connected.");
    if (qp.get("google_error")) toast.error(`Google error: ${qp.get("google_error")}`);
    if (qp.get("tiktok_connected")) toast.success("TikTok connected.");
    if (qp.get("tiktok_error")) toast.error(`TikTok error: ${qp.get("tiktok_error")}`);
    if (qp.get("youtube_connected")) toast.success("YouTube connected.");
    if (qp.get("youtube_error")) toast.error(`YouTube error: ${qp.get("youtube_error")}`);
    if (qp.get("linkedin_connected")) toast.success("LinkedIn connected.");
    if (qp.get("linkedin_error")) toast.error(`LinkedIn error: ${qp.get("linkedin_error")}`);
    if (qp.get("threads_connected")) toast.success("Threads connected.");
    if (qp.get("threads_error")) toast.error(`Threads error: ${qp.get("threads_error")}`);
    if (qp.get("pinterest_connected")) toast.success("Pinterest connected.");
    if (qp.get("pinterest_error")) toast.error(`Pinterest error: ${qp.get("pinterest_error")}`);
    const FLAGS = ["meta_connected", "meta_error", "google_connected", "google_error", "tiktok_connected", "tiktok_error", "youtube_connected", "youtube_error", "linkedin_connected", "linkedin_error", "threads_connected", "threads_error", "pinterest_connected", "pinterest_error"];
    if (FLAGS.some((k) => qp.has(k))) {
      const url = new URL(window.location.href);
      FLAGS.forEach((k) => url.searchParams.delete(k));
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  const connectStub = async (id) => {
    setBusy(id);
    try {
      await api.post("/connections/connect", { channel: id, handle: handle[id] || "" });
      toast.success("Connected.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not connect.");
    } finally {
      setBusy(null);
    }
  };

  const disconnect = async (id) => {
    setBusy(id);
    try {
      await api.post("/connections/disconnect", { channel: id });
      toast.success("Disconnected.");
      load();
    } finally {
      setBusy(null);
    }
  };

  const disconnectGoogle = async () => {
    setBusy("google_drive");
    try {
      await api.post("/integrations/google/disconnect");
      toast.success("Google Drive disconnected.");
      load();
    } finally {
      setBusy(null);
    }
  };

  const disconnectTiktok = async () => {
    setBusy("tiktok");
    try {
      await api.post("/connections/tiktok/disconnect");
      toast.success("TikTok disconnected.");
      load();
    } finally {
      setBusy(null);
    }
  };

  const disconnectYoutube = async () => {
    setBusy("youtube");
    try {
      await api.post("/connections/youtube/disconnect");
      toast.success("YouTube disconnected.");
      load();
    } finally {
      setBusy(null);
    }
  };

  const disconnectLinkedin = async () => {
    setBusy("linkedin");
    try {
      await api.post("/connections/linkedin/disconnect");
      toast.success("LinkedIn disconnected.");
      load();
    } finally {
      setBusy(null);
    }
  };

  const disconnectThreads = async () => {
    setBusy("threads");
    try {
      await api.post("/connections/threads/disconnect");
      toast.success("Threads disconnected.");
      load();
    } finally {
      setBusy(null);
    }
  };

  const disconnectPinterest = async () => {
    setBusy("pinterest");
    try {
      await api.post("/connections/pinterest/disconnect");
      toast.success("Pinterest disconnected.");
      load();
    } finally {
      setBusy(null);
    }
  };

  const metaConfigured = metaStatus?.configured;
  const googleConfigured = googleStatus?.configured;
  const tiktokConfigured = tiktokStatus?.configured;
  const tiktokConnected = !!tiktokStatus?.connected;
  const youtubeConfigured = youtubeStatus?.configured;
  const youtubeConnected = !!youtubeStatus?.connected;
  const ytChannel = youtubeStatus?.channel;
  const linkedinConfigured = linkedinStatus?.configured;
  const linkedinConnected = !!linkedinStatus?.connected;
  const threadsConfigured = threadsStatus?.configured;
  const threadsConnected = !!threadsStatus?.connected;
  const pinterestConfigured = pinterestStatus?.configured;
  const pinterestConnected = !!pinterestStatus?.connected;
  const fbConn = connections.facebook;
  const igConn = connections.instagram;
  const driveConnected = !!googleStatus?.connected;

  const metaPagesSummary = useMemo(() => {
    if (!metaStatus) return null;
    const pages = metaStatus.facebook?.pages || [];
    const igs = metaStatus.instagram?.accounts || [];
    return { pages, igs };
  }, [metaStatus]);

  return (
    <div className="space-y-6" data-testid="connections-panel">
      <p className="text-slate-400 text-sm max-w-xl">
        Connect your social accounts and tools. Instagram & Facebook are live via
        Meta OAuth — the DM Closer and schedulers fire on those channels once
        connected. Google Drive lets your Content Engine push posts straight to
        your Drive.
      </p>

      {/* META — Instagram + Facebook */}
      <div
        className="p-5 bg-[#0A192F] border border-cyan-400/30 rounded-sm cf-glow"
        data-testid="meta-block"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3">
              <Plug size={16} className="text-cyan-300" />
              <p className="font-heading font-bold text-white text-lg">
                Instagram + Facebook
              </p>
              {fbConn || igConn ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-400/10 text-emerald-300 text-[10px] font-mono uppercase tracking-widest border border-emerald-400/30">
                  <Check size={10} /> Connected
                </span>
              ) : metaConfigured ? (
                <span className="px-2 py-0.5 bg-white/5 text-slate-400 text-[10px] font-mono uppercase tracking-widest border border-white/10">
                  Ready
                </span>
              ) : (
                <span className="px-2 py-0.5 bg-amber-400/10 text-amber-300 text-[10px] font-mono uppercase tracking-widest border border-amber-400/30">
                  Setup pending
                </span>
              )}
            </div>
            <p className="mt-2 text-slate-400 text-sm max-w-xl">
              One click. We&apos;ll list every page + IG business account you manage,
              grab a long-lived access token, and subscribe to the Meta webhook so
              your DM Closer fires in real time.
            </p>
          </div>

          <div className="flex items-center gap-2">
            {fbConn || igConn ? (
              <>
                <a
                  href={META_LOGIN_URL}
                  data-testid="meta-reconnect-btn"
                  className="px-4 py-2 bg-transparent border border-cyan-400/40 text-cyan-300 font-bold text-xs uppercase tracking-widest rounded-sm hover:bg-cyan-400/10 flex items-center gap-1"
                >
                  Re-authorize <ExternalLink size={12} />
                </a>
                <button
                  onClick={() => {
                    if (fbConn) disconnect("facebook");
                    if (igConn) disconnect("instagram");
                  }}
                  data-testid="meta-disconnect-btn"
                  disabled={busy === "facebook" || busy === "instagram"}
                  className="text-xs text-red-300 hover:text-red-200 px-3 py-2"
                >
                  Disconnect
                </button>
              </>
            ) : (
              <a
                href={metaConfigured ? META_LOGIN_URL : undefined}
                onClick={(e) => {
                  if (!metaConfigured) {
                    e.preventDefault();
                    toast.error("Meta app not configured on the server yet.");
                  }
                }}
                data-testid="meta-connect-btn"
                className={`px-5 py-2.5 font-bold text-xs uppercase tracking-widest rounded-sm flex items-center gap-2 ${
                  metaConfigured
                    ? "bg-[#00F0FF] text-[#050B14] hover:bg-[#33F3FF] shadow-[0_0_30px_rgba(0,240,255,0.4)]"
                    : "bg-white/5 text-slate-500 cursor-not-allowed"
                }`}
              >
                <Plus size={14} /> Connect Facebook &amp; Instagram
              </a>
            )}
          </div>
        </div>

        {metaPagesSummary && (metaPagesSummary.pages.length > 0 || metaPagesSummary.igs.length > 0) && (
          <div className="mt-4 grid sm:grid-cols-2 gap-3" data-testid="meta-asset-list">
            {metaPagesSummary.pages.map((p) => (
              <div
                key={`fb-${p.page_id}`}
                className="px-3 py-2 bg-[#050B14] border border-white/5 rounded-sm text-sm flex items-center justify-between"
              >
                <span className="text-slate-200 truncate">📘 {p.page_name || p.page_id}</span>
                {p.webhook_subscribed && (
                  <span className="text-emerald-300 text-[10px] font-mono uppercase tracking-widest">live</span>
                )}
              </div>
            ))}
            {metaPagesSummary.igs.map((a) => (
              <div
                key={`ig-${a.ig_user_id}`}
                className="px-3 py-2 bg-[#050B14] border border-white/5 rounded-sm text-sm flex items-center justify-between"
              >
                <span className="text-slate-200 truncate">📸 @{a.username || a.ig_user_id}</span>
                {a.webhook_subscribed && (
                  <span className="text-emerald-300 text-[10px] font-mono uppercase tracking-widest">live</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* GOOGLE DRIVE */}
      <div
        className="p-5 bg-[#0A192F] border border-white/10 rounded-sm"
        data-testid="google-drive-block"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3">
              <Plug size={16} className="text-cyan-300" />
              <p className="font-heading font-bold text-white text-lg">Google Drive</p>
              {driveConnected ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-400/10 text-emerald-300 text-[10px] font-mono uppercase tracking-widest border border-emerald-400/30">
                  <Check size={10} /> Connected
                </span>
              ) : googleConfigured ? (
                <span className="px-2 py-0.5 bg-white/5 text-slate-400 text-[10px] font-mono uppercase tracking-widest border border-white/10">
                  Ready
                </span>
              ) : (
                <span className="px-2 py-0.5 bg-amber-400/10 text-amber-300 text-[10px] font-mono uppercase tracking-widest border border-amber-400/30">
                  Setup pending
                </span>
              )}
            </div>
            <p className="mt-2 text-slate-400 text-sm max-w-xl">
              Push any Content Engine post straight to your Drive as a Doc or
              Markdown file. We only get the <code className="text-cyan-200">drive.file</code> scope — we only
              ever see what we create for you.
            </p>
            {driveConnected && googleStatus?.email && (
              <p className="mt-2 text-slate-300 text-xs font-mono">
                Connected as <span className="text-cyan-300">{googleStatus.email}</span>
              </p>
            )}
          </div>

          <div className="flex items-center gap-2">
            {driveConnected ? (
              <button
                onClick={disconnectGoogle}
                disabled={busy === "google_drive"}
                data-testid="google-disconnect-btn"
                className="text-xs text-red-300 hover:text-red-200 px-3 py-2"
              >
                {busy === "google_drive" ? <Loader2 size={14} className="animate-spin" /> : "Disconnect"}
              </button>
            ) : (
              <a
                href={googleConfigured ? GOOGLE_LOGIN_URL : undefined}
                onClick={(e) => {
                  if (!googleConfigured) {
                    e.preventDefault();
                    toast.error("Google integration not configured on the server yet.");
                  }
                }}
                data-testid="google-connect-btn"
                className={`px-5 py-2.5 font-bold text-xs uppercase tracking-widest rounded-sm flex items-center gap-2 ${
                  googleConfigured
                    ? "bg-white text-[#050B14] hover:bg-slate-100"
                    : "bg-white/5 text-slate-500 cursor-not-allowed"
                }`}
              >
                <Plus size={14} /> Connect Google Drive
              </a>
            )}
          </div>
        </div>
      </div>

      {/* TIKTOK */}
      <div
        className="p-5 bg-[#0A192F] border border-white/10 rounded-sm"
        data-testid="tiktok-block"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3">
              <Plug size={16} className="text-cyan-300" />
              <p className="font-heading font-bold text-white text-lg">TikTok</p>
              {tiktokConnected ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-400/10 text-emerald-300 text-[10px] font-mono uppercase tracking-widest border border-emerald-400/30">
                  <Check size={10} /> Connected
                </span>
              ) : tiktokConfigured ? (
                <span className="px-2 py-0.5 bg-white/5 text-slate-400 text-[10px] font-mono uppercase tracking-widest border border-white/10">
                  Ready
                </span>
              ) : (
                <span className="px-2 py-0.5 bg-amber-400/10 text-amber-300 text-[10px] font-mono uppercase tracking-widest border border-amber-400/30">
                  Setup pending
                </span>
              )}
            </div>
            <p className="mt-2 text-slate-400 text-sm max-w-xl">
              Connect your TikTok account to read videos &amp; schedule posts.
              We request basic info, video list, upload, and publish scopes.
            </p>
            {tiktokConnected && tiktokStatus?.display_name && (
              <p className="mt-2 text-slate-300 text-xs font-mono">
                Connected as <span className="text-cyan-300">@{tiktokStatus.display_name}</span>
              </p>
            )}
          </div>

          <div className="flex items-center gap-2">
            {tiktokConnected ? (
              <button
                onClick={disconnectTiktok}
                disabled={busy === "tiktok"}
                data-testid="tiktok-disconnect-btn"
                className="text-xs text-red-300 hover:text-red-200 px-3 py-2"
              >
                {busy === "tiktok" ? <Loader2 size={14} className="animate-spin" /> : "Disconnect"}
              </button>
            ) : (
              <a
                href={tiktokConfigured ? TIKTOK_LOGIN_URL : undefined}
                onClick={(e) => {
                  if (!tiktokConfigured) {
                    e.preventDefault();
                    toast.error("TikTok integration not configured on the server yet.");
                  }
                }}
                data-testid="tiktok-connect-btn"
                className={`px-5 py-2.5 font-bold text-xs uppercase tracking-widest rounded-sm flex items-center gap-2 ${
                  tiktokConfigured
                    ? "bg-[#FE2C55] text-white hover:bg-[#FF4A6B] shadow-[0_0_30px_rgba(254,44,85,0.3)]"
                    : "bg-white/5 text-slate-500 cursor-not-allowed"
                }`}
              >
                <Plus size={14} /> Connect TikTok
              </a>
            )}
          </div>
        </div>
      </div>

      {/* YOUTUBE */}
      <div
        className="p-5 bg-[#0A192F] border border-white/10 rounded-sm"
        data-testid="youtube-block"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3">
              <Plug size={16} className="text-cyan-300" />
              <p className="font-heading font-bold text-white text-lg">YouTube</p>
              {youtubeConnected ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-400/10 text-emerald-300 text-[10px] font-mono uppercase tracking-widest border border-emerald-400/30">
                  <Check size={10} /> Connected
                </span>
              ) : youtubeConfigured ? (
                <span className="px-2 py-0.5 bg-white/5 text-slate-400 text-[10px] font-mono uppercase tracking-widest border border-white/10">
                  Ready
                </span>
              ) : (
                <span className="px-2 py-0.5 bg-amber-400/10 text-amber-300 text-[10px] font-mono uppercase tracking-widest border border-amber-400/30">
                  Setup pending
                </span>
              )}
            </div>
            <p className="mt-2 text-slate-400 text-sm max-w-xl">
              Connect your YouTube channel to track subscribers, views and your
              latest uploads. Read-only — we request the{" "}
              <code className="text-cyan-200">youtube.readonly</code> scope only.
            </p>
            {youtubeConnected && ytChannel && (
              <div
                className="mt-3 flex items-center gap-3"
                data-testid="youtube-channel-summary"
              >
                {ytChannel.thumbnail && (
                  <img
                    src={ytChannel.thumbnail}
                    alt=""
                    className="h-10 w-10 rounded-full border border-white/10"
                  />
                )}
                <div className="text-xs">
                  <p className="text-cyan-300 font-mono">{ytChannel.title}</p>
                  <p className="text-slate-400 mt-0.5">
                    {ytChannel.hidden_subscriber_count
                      ? "Subs hidden"
                      : `${Number(ytChannel.subscriber_count).toLocaleString()} subs`}{" "}
                    · {Number(ytChannel.video_count).toLocaleString()} videos ·{" "}
                    {Number(ytChannel.view_count).toLocaleString()} views
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            {youtubeConnected ? (
              <button
                onClick={disconnectYoutube}
                disabled={busy === "youtube"}
                data-testid="youtube-disconnect-btn"
                className="text-xs text-red-300 hover:text-red-200 px-3 py-2"
              >
                {busy === "youtube" ? <Loader2 size={14} className="animate-spin" /> : "Disconnect"}
              </button>
            ) : (
              <a
                href={youtubeConfigured ? YOUTUBE_LOGIN_URL : undefined}
                onClick={(e) => {
                  if (!youtubeConfigured) {
                    e.preventDefault();
                    toast.error("YouTube integration not configured on the server yet.");
                  }
                }}
                data-testid="youtube-connect-btn"
                className={`px-5 py-2.5 font-bold text-xs uppercase tracking-widest rounded-sm flex items-center gap-2 ${
                  youtubeConfigured
                    ? "bg-[#FF0000] text-white hover:bg-[#FF2A2A] shadow-[0_0_30px_rgba(255,0,0,0.3)]"
                    : "bg-white/5 text-slate-500 cursor-not-allowed"
                }`}
              >
                <Plus size={14} /> Connect YouTube
              </a>
            )}
          </div>
        </div>
      </div>

      {/* LINKEDIN */}
      <div
        className="p-5 bg-[#0A192F] border border-white/10 rounded-sm"
        data-testid="linkedin-block"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3">
              <Plug size={16} className="text-cyan-300" />
              <p className="font-heading font-bold text-white text-lg">LinkedIn</p>
              {linkedinConnected ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-400/10 text-emerald-300 text-[10px] font-mono uppercase tracking-widest border border-emerald-400/30">
                  <Check size={10} /> Connected
                </span>
              ) : linkedinConfigured ? (
                <span className="px-2 py-0.5 bg-white/5 text-slate-400 text-[10px] font-mono uppercase tracking-widest border border-white/10">
                  Ready
                </span>
              ) : (
                <span className="px-2 py-0.5 bg-amber-400/10 text-amber-300 text-[10px] font-mono uppercase tracking-widest border border-amber-400/30">
                  Setup pending
                </span>
              )}
            </div>
            <p className="mt-2 text-slate-400 text-sm max-w-xl">
              Connect LinkedIn to publish posts straight to your profile from the
              Content Engine&apos;s Omni-Publish. We request sign-in +{" "}
              <code className="text-cyan-200">w_member_social</code> (post on your behalf).
            </p>
            {linkedinConnected && linkedinStatus?.name && (
              <p className="mt-2 text-slate-300 text-xs font-mono">
                Connected as <span className="text-cyan-300">{linkedinStatus.name}</span>
              </p>
            )}
          </div>

          <div className="flex items-center gap-2">
            {linkedinConnected ? (
              <button
                onClick={disconnectLinkedin}
                disabled={busy === "linkedin"}
                data-testid="linkedin-disconnect-btn"
                className="text-xs text-red-300 hover:text-red-200 px-3 py-2"
              >
                {busy === "linkedin" ? <Loader2 size={14} className="animate-spin" /> : "Disconnect"}
              </button>
            ) : (
              <a
                href={linkedinConfigured ? LINKEDIN_LOGIN_URL : undefined}
                onClick={(e) => {
                  if (!linkedinConfigured) {
                    e.preventDefault();
                    toast.error("LinkedIn integration not configured on the server yet.");
                  }
                }}
                data-testid="linkedin-connect-btn"
                className={`px-5 py-2.5 font-bold text-xs uppercase tracking-widest rounded-sm flex items-center gap-2 ${
                  linkedinConfigured
                    ? "bg-[#0A66C2] text-white hover:bg-[#1275d6] shadow-[0_0_30px_rgba(10,102,194,0.35)]"
                    : "bg-white/5 text-slate-500 cursor-not-allowed"
                }`}
              >
                <Plus size={14} /> Connect LinkedIn
              </a>
            )}
          </div>
        </div>
      </div>

      {/* THREADS */}
      <div
        className="p-5 bg-[#0A192F] border border-white/10 rounded-sm"
        data-testid="threads-block"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3">
              <Plug size={16} className="text-cyan-300" />
              <p className="font-heading font-bold text-white text-lg">Threads</p>
              {threadsConnected ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-400/10 text-emerald-300 text-[10px] font-mono uppercase tracking-widest border border-emerald-400/30">
                  <Check size={10} /> Connected
                </span>
              ) : threadsConfigured ? (
                <span className="px-2 py-0.5 bg-white/5 text-slate-400 text-[10px] font-mono uppercase tracking-widest border border-white/10">
                  Ready
                </span>
              ) : (
                <span className="px-2 py-0.5 bg-amber-400/10 text-amber-300 text-[10px] font-mono uppercase tracking-widest border border-amber-400/30">
                  Setup pending
                </span>
              )}
            </div>
            <p className="mt-2 text-slate-400 text-sm max-w-xl">
              Connect Threads to publish posts straight from the Content
              Engine&apos;s Omni-Publish.
            </p>
            {threadsConnected && threadsStatus?.username && (
              <p className="mt-2 text-slate-300 text-xs font-mono">
                Connected as <span className="text-cyan-300">@{threadsStatus.username}</span>
              </p>
            )}
          </div>

          <div className="flex items-center gap-2">
            {threadsConnected ? (
              <button
                onClick={disconnectThreads}
                disabled={busy === "threads"}
                data-testid="threads-disconnect-btn"
                className="text-xs text-red-300 hover:text-red-200 px-3 py-2"
              >
                {busy === "threads" ? <Loader2 size={14} className="animate-spin" /> : "Disconnect"}
              </button>
            ) : (
              <a
                href={threadsConfigured ? THREADS_LOGIN_URL : undefined}
                onClick={(e) => {
                  if (!threadsConfigured) {
                    e.preventDefault();
                    toast.error("Threads integration not configured on the server yet.");
                  }
                }}
                data-testid="threads-connect-btn"
                className={`px-5 py-2.5 font-bold text-xs uppercase tracking-widest rounded-sm flex items-center gap-2 ${
                  threadsConfigured
                    ? "bg-white text-black hover:bg-slate-200"
                    : "bg-white/5 text-slate-500 cursor-not-allowed"
                }`}
              >
                <Plus size={14} /> Connect Threads
              </a>
            )}
          </div>
        </div>
      </div>

      {/* PINTEREST */}
      <div
        className="p-5 bg-[#0A192F] border border-white/10 rounded-sm"
        data-testid="pinterest-block"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3">
              <Plug size={16} className="text-cyan-300" />
              <p className="font-heading font-bold text-white text-lg">Pinterest</p>
              {pinterestConnected ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-400/10 text-emerald-300 text-[10px] font-mono uppercase tracking-widest border border-emerald-400/30">
                  <Check size={10} /> Connected
                </span>
              ) : pinterestConfigured ? (
                <span className="px-2 py-0.5 bg-white/5 text-slate-400 text-[10px] font-mono uppercase tracking-widest border border-white/10">
                  Ready
                </span>
              ) : (
                <span className="px-2 py-0.5 bg-amber-400/10 text-amber-300 text-[10px] font-mono uppercase tracking-widest border border-amber-400/30">
                  Setup pending
                </span>
              )}
            </div>
            <p className="mt-2 text-slate-400 text-sm max-w-xl">
              Connect Pinterest to pin your images straight to your boards from the
              Content Engine.
            </p>
            {pinterestConnected && pinterestStatus?.username && (
              <p className="mt-2 text-slate-300 text-xs font-mono">
                Connected as <span className="text-cyan-300">@{pinterestStatus.username}</span>
              </p>
            )}
          </div>

          <div className="flex items-center gap-2">
            {pinterestConnected ? (
              <button
                onClick={disconnectPinterest}
                disabled={busy === "pinterest"}
                data-testid="pinterest-disconnect-btn"
                className="text-xs text-red-300 hover:text-red-200 px-3 py-2"
              >
                {busy === "pinterest" ? <Loader2 size={14} className="animate-spin" /> : "Disconnect"}
              </button>
            ) : (
              <a
                href={pinterestConfigured ? PINTEREST_LOGIN_URL : undefined}
                onClick={(e) => {
                  if (!pinterestConfigured) {
                    e.preventDefault();
                    toast.error("Pinterest integration not configured on the server yet.");
                  }
                }}
                data-testid="pinterest-connect-btn"
                className={`px-5 py-2.5 font-bold text-xs uppercase tracking-widest rounded-sm flex items-center gap-2 ${
                  pinterestConfigured
                    ? "bg-white text-black hover:bg-slate-200"
                    : "bg-white/5 text-slate-500 cursor-not-allowed"
                }`}
              >
                <Plus size={14} /> Connect Pinterest
              </a>
            )}
          </div>
        </div>
      </div>

      {/* OTHER CHANNELS (stub / coming soon) */}
      <div>
        <h3 className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-3">
          More channels
        </h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {channels
            .filter((c) => c.via !== "meta" && c.via !== "tiktok" && c.via !== "youtube" && c.via !== "linkedin" && c.via !== "threads" && c.via !== "pinterest")
            .map((c) => {
              const conn = connections[c.id];
              const available = c.status === "available";
              return (
                <div
                  key={c.id}
                  data-testid={`connection-card-${c.id}`}
                  className="p-4 bg-[#0A192F] border border-white/10 rounded-sm"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Plug size={14} className="text-slate-500" />
                      <p className="font-heading font-bold text-white text-sm">{c.label}</p>
                    </div>
                    {conn ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-400/10 text-emerald-300 text-[9px] font-mono uppercase tracking-widest border border-emerald-400/30">
                        <Check size={10} /> On
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 bg-white/5 text-slate-500 text-[9px] font-mono uppercase tracking-widest border border-white/10">
                        {available ? "Available" : "Soon"}
                      </span>
                    )}
                  </div>

                  {available && !conn && (
                    <div className="mt-3 flex gap-2">
                      <input
                        value={handle[c.id] || ""}
                        onChange={(e) => setHandle({ ...handle, [c.id]: e.target.value })}
                        placeholder={`@your_${c.id}_handle`}
                        data-testid={`connection-input-${c.id}`}
                        className="flex-1 px-2 py-1.5 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-xs rounded-sm"
                      />
                      <button
                        onClick={() => connectStub(c.id)}
                        disabled={busy === c.id}
                        data-testid={`connection-connect-${c.id}`}
                        className="px-3 py-1.5 bg-[#00F0FF] text-[#050B14] font-bold text-[10px] uppercase tracking-widest rounded-sm hover:bg-[#33F3FF] disabled:opacity-60"
                      >
                        Add
                      </button>
                    </div>
                  )}

                  {conn && (
                    <div className="mt-3 flex items-center justify-between">
                      <p className="text-slate-300 text-xs truncate">{conn.handle || "(no handle)"}</p>
                      <button
                        onClick={() => disconnect(c.id)}
                        disabled={busy === c.id}
                        data-testid={`connection-disconnect-${c.id}`}
                        className="text-[10px] text-red-300 hover:text-red-200"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      </div>

      <BlotatoCard />
      <ApiKeys />
    </div>
  );
};

export default Connections;
